import React, { useMemo } from "react";
import {
  Compass,
  ChevronRight,
  Users,
  MessageSquareText,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* -------------------- image resolver -------------------- */
function resolveImageUrl(url) {
  if (!url) return null;
  let s = String(url).trim();
  if (!s) return null;
  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^https?:\/\//i.test(s)) return s;

  const r2Base = process.env.REACT_APP_R2_PUBLIC_BASE || "";
  if (r2Base && /^clubs\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }

  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = process.env.REACT_APP_API_BASE || "";
  return apiBase ? `${apiBase.replace(/\/+$/, "")}${rooted}` : rooted;
}

/* -------------------- getters -------------------- */
const getClubSlug = (c) => c?.slug ?? c?.club_slug ?? c?.clubSlug ?? null;
const getClubName = (c) => c?.name ?? c?.title ?? "Club";
const getClubDesc = (c) =>
  c?.tagline ??
  c?.subtitle ??
  c?.headline ??
  c?.description ??
  c?.status ??
  "";

const getClubAvatar = (c) =>
  resolveImageUrl(
    c?.avatar_image_url ??
      c?.avatarImageUrl ??
      c?.icon_url ??
      c?.iconUrl ??
      null
  );

const getMembersCount = (c) => {
  const n = Number(
    c?.member_count ??
      c?.members_count ??
      c?.membersCount ??
      c?.memberCount ??
      null
  );
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const getPostsThisWeek = (c) => {
  const n = Number(
    c?.posts_week ??
      c?.postsThisWeek ??
      c?.posts_this_week ??
      c?.weekly_posts ??
      null
  );
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const getActiveNow = (c) => {
  const n = Number(
    c?.active_now ??
      c?.activeNow ??
      c?.online_now ??
      c?.onlineNow ??
      null
  );
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const getLastActivityAt = (c) =>
  c?.last_activity_at ??
  c?.lastActivityAt ??
  c?.updated_at ??
  c?.updatedAt ??
  null;

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("") || "C";
}

function fmtCompact(n) {
  if (!Number.isFinite(n)) return "";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}m`;
}

function timeAgo(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  if (diff < 0) return null;

  const m = Math.floor(diff / 60000);
  if (m < 1) return "Active just now";
  if (m < 60) return `Active ${m}m ago`;

  const h = Math.floor(m / 60);
  if (h < 24) return `Active ${h}h ago`;

  const days = Math.floor(h / 24);
  if (days < 7) return `Active ${days}d ago`;

  return null;
}

/**
 * CommunityClubsBar – final alignment pass
 *
 * Changes per request:
 * Avatar moved to LEFT
 * "Active X ago" sits to the RIGHT of avatar
 * Only label is "Club" with WHITE bordered pill
 * Header status dot now has a CIRCLE RING around it (matches your ref)
 * Everything else unchanged
 */
export default function CommunityClubsBar({
  clubs = [],
  title = "Clubs",
  subtitle = "DISCOVER",
  maxItems = 4,
  className = "",
  onOpenClub,
  showBrowseAll = true,
  browseAllLabel = "Browse all clubs",
  browseAllTo = "/clubs",
}) {
  const navigate = useNavigate();

  const items = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(clubs) ? clubs : []).filter((c) => {
      const slug = getClubSlug(c);
      if (!slug || seen.has(slug)) return false;
      seen.add(slug);
      return seen.size <= maxItems;
    });
  }, [clubs, maxItems]);

  if (!items.length) return null;

  const shell =
    "rounded-3xl border border-white/12 bg-black/35 backdrop-blur-2xl ring-1 ring-white/10 overflow-hidden";
  const headerBar = "border-b border-white/10 bg-black/25";
  const iconBox =
    "h-9 w-9 rounded-2xl border border-white/12 bg-black/25 grid place-items-center";
  const innerCard =
    "relative rounded-2xl border border-white/10 bg-black/25 hover:bg-black/30 hover:border-white/15 transition-colors";

  return (
    <section className={className}>
      <div className={shell}>
        {/* Header */}
        <div className={cn("px-4 py-3.5", headerBar)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={iconBox}>
                <Compass className="h-[18px] w-[18px] text-emerald-200" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-white">{title}</p>
                <p className="text-[11px] text-white/45">{subtitle}</p>
              </div>
            </div>

            {/* Ringed status dot (matches your reference) */}
            <div className="h-7 w-7 rounded-full border border-white/55 bg-black/10 grid place-items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300/85" />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-3 py-3 space-y-3 max-h-[480px] overflow-y-auto scrollbar-hide">
          {items.map((c) => {
            const slug = getClubSlug(c);
            const name = getClubName(c);
            const desc = getClubDesc(c);
            const avatar = getClubAvatar(c);
            const lastLine = timeAgo(getLastActivityAt(c));
            const activeNow = getActiveNow(c);
            const members = getMembersCount(c);
            const postsWeek = getPostsThisWeek(c);

            return (
              <div
                key={slug}
                className={innerCard}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/clubs/${slug}`)}
              >
                <div className="absolute left-0 top-0 h-full w-[2px] bg-white/14" />

                <div className="p-4">
                  {/* TOP ROW */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Avatar LEFT */}
                      {avatar ? (
                        <img
                          src={avatar}
                          alt={name}
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-white/85"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full ring-2 ring-white/85 grid place-items-center text-[11px] font-semibold text-white">
                          {initials(name)}
                        </div>
                      )}

                      {/* Club pill + activity */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-[2px] text-[10px] rounded-full border border-white/70 text-white/80 font-medium">
                            Club
                          </span>
                          {lastLine && (
                            <span className="text-[8.8px] text-white/45 leading-tight">
                              {lastLine}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="h-4 w-4 text-cyan-400/70" />
                  </div>

                  {/* Title + desc */}
                  <div className="mt-2">
                    <p className="text-[15px] font-semibold text-white/85 truncate">
                      {name}
                    </p>
                    <p className="text-[11.5px] text-white/60 mt-1.5 line-clamp-2">
                      {desc}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="mt-3 flex items-center justify-between">
                    {typeof activeNow === "number" ? (
                      <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-2.5 py-[4px]">
                        <Zap className="h-3.5 w-3.5 text-emerald-200" />
                        <span className="text-[10.5px] text-white/75 font-medium">
                          {fmtCompact(activeNow)} now
                        </span>
                      </div>
                    ) : (
                      <div />
                    )}

                    <div className="flex items-center gap-3 text-white/55">
                      {typeof members === "number" && (
                        <div className="flex items-center gap-1 text-[10.5px]">
                          <Users className="h-3.5 w-3.5" />
                          {fmtCompact(members)}
                        </div>
                      )}
                      {typeof postsWeek === "number" && (
                        <div className="flex items-center gap-1 text-[10.5px]">
                          <MessageSquareText className="h-3.5 w-3.5" />
                          {fmtCompact(postsWeek)}/wk
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {showBrowseAll && (
            <button
              onClick={() => navigate(browseAllTo)}
              className="w-full mt-2 rounded-2xl border border-white/12 bg-black/25 px-3 py-2.5 text-left hover:bg-black/30 hover:border-white/15 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-semibold text-white/80">
                    {browseAllLabel}
                  </p>
                  <p className="text-[11px] text-white/45">
                    See what’s growing across AnimeHouse.
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-white/50" />
              </div>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
